import React, { useEffect, useState } from "react";
import {
  CallToAction,
  ChatBubble,
  PromptToChat,
  StatusUpdate,
} from "@tomis-tech/chat-ui";

import { trackEvent, type TomisEventName } from "@/tracking";
import { ChatWidgetAnnouncement } from "@/types";

function trackAnnouncement(
  eventName: TomisEventName,
  announcement: ChatWidgetAnnouncement,
) {
  trackEvent(eventName, {
    announcement_id: announcement.id,
    display_type: announcement.displayType,
  });
}

interface AnnouncementProps {
  announcement: ChatWidgetAnnouncement;
  position: "left" | "right";
  onOpen: () => void;
  onSend: (message: string) => void;
  onDismiss: () => void;
}

export const Announcement: React.FC<AnnouncementProps> = ({
  announcement,
  position,
  onOpen,
  onSend,
  onDismiss,
}) => {
  const [trackedImpressionId, setTrackedImpressionId] = useState<number | null>(
    null,
  );

  function getPlacement() {
    if (announcement.placement === "top" || announcement.placement === "side") {
      return announcement.placement;
    }

    if (announcement.displayType === "status_update") {
      return "top";
    }

    return undefined;
  }

  const placement = getPlacement();

  useEffect(() => {
    if (placement !== "top" && placement !== "side") {
      return;
    }
    if (trackedImpressionId === announcement.id) {
      return;
    }

    trackAnnouncement("announcement_impression", announcement);
    setTrackedImpressionId(announcement.id);
  }, [placement, trackedImpressionId, announcement]);

  function handleDismiss() {
    trackAnnouncement("announcement_dismiss", announcement);

    onDismiss();
  }

  function handleCallToActionClick() {
    trackAnnouncement("announcement_click", announcement);
  }

  function handleOpenChat() {
    trackAnnouncement("announcement_click", announcement);
    trackAnnouncement("announcement_chat_opened", announcement);

    onOpen();
  }

  function handlePromptSend(text: string) {
    trackAnnouncement("announcement_click", announcement);
    trackAnnouncement("announcement_chat_opened", announcement);

    onOpen();
    onSend(text);
  }

  if (!placement) {
    return null;
  }

  const colors = {
    primaryColor: announcement.primaryColor,
    secondaryColor: announcement.secondaryColor,
  };

  switch (announcement.displayType) {
    case "call_to_action":
      return (
        <CallToAction
          {...colors}
          description={announcement.description}
          buttonTitle={announcement.buttonTitle}
          destinationLink={announcement.destinationLink}
          placement={placement}
          position={position}
          onClick={handleCallToActionClick}
          onDismiss={handleDismiss}
        />
      );
    case "prompt_to_chat":
      return (
        <PromptToChat
          {...colors}
          message={announcement.message}
          placement={placement}
          position={position}
          onSend={handlePromptSend}
          onDismiss={handleDismiss}
        />
      );
    case "chat_bubble":
      return (
        <ChatBubble
          {...colors}
          message={announcement.message}
          placement={placement}
          position={position}
          onClick={handleOpenChat}
          onDismiss={handleDismiss}
        />
      );
    case "status_update":
      return (
        <StatusUpdate
          {...colors}
          statusTitle={announcement.statusTitle}
          message={announcement.message}
          placement={placement}
          position={position}
          onClick={handleOpenChat}
          onDismiss={handleDismiss}
        />
      );
    default:
      return null;
  }
};
